import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  private promptEvent: any;
  private isStandaloneSubject = new BehaviorSubject<boolean>(false);
  private isInstalledSubject = new BehaviorSubject<boolean>(false);
  private isOnlineSubject = new BehaviorSubject<boolean>(true);
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  private isBrowser: boolean;

  public isStandalone$: Observable<boolean> = this.isStandaloneSubject.asObservable();
  public isInstalled$: Observable<boolean> = this.isInstalledSubject.asObservable();
  public isOnline$: Observable<boolean> = this.isOnlineSubject.asObservable();
  public updateAvailable$: Observable<boolean> = this.updateAvailableSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    
    if (this.isBrowser) {
      this.isOnlineSubject.next(navigator.onLine);
      this.checkIfStandalone();
      this.listenForInstallPrompt();
      this.listenForOnlineStatus();
      this.checkForUpdates();
    }
  }

  /**
   * Check if app is running in standalone mode
   */
  private checkIfStandalone(): void {
    if (!this.isBrowser) return;
    
    const isStandalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone ||
      document.referrer.includes('android-app://');
    
    this.isStandaloneSubject.next(isStandalone);
    console.log('[PWA Service] Is standalone:', isStandalone);
  }

  /**
   * Listen for the beforeinstallprompt event
   */
  private listenForInstallPrompt(): void {
    if (!this.isBrowser) return;
    
    window.addEventListener('beforeinstallprompt', (e: any) => {
      console.log('[PWA Service] Install prompt available');
      e.preventDefault();
      this.promptEvent = e;
      this.isInstalledSubject.next(false);
    });

    window.addEventListener('appinstalled', () => {
      console.log('[PWA Service] App installed');
      this.isInstalledSubject.next(true);
      this.promptEvent = null;
    });
  }

  /**
   * Listen for online/offline status changes
   */
  private listenForOnlineStatus(): void {
    if (!this.isBrowser) return;
    
    fromEvent(window, 'online').subscribe(() => {
      console.log('[PWA Service] App is online');
      this.isOnlineSubject.next(true);
    });

    fromEvent(window, 'offline').subscribe(() => {
      console.log('[PWA Service] App is offline');
      this.isOnlineSubject.next(false);
    });
  }

  /**
   * Check for service worker updates
   */
  private checkForUpdates(): void {
    if (!this.isBrowser || !('serviceWorker' in navigator)) return;
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        // Check for updates every hour
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);

        // Listen for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('[PWA Service] Update available');
                this.updateAvailableSubject.next(true);
              }
            });
          }
        });
      });
    }
  }

  /**
   * Register the service worker
   */
  public registerServiceWorker(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isBrowser) {
        resolve();
        return;
      }
      
      if ('serviceWorker' in navigator) {
        window.addEventListener('load', () => {
          navigator.serviceWorker
            .register('/service-worker.js', { scope: '/' })
            .then((registration) => {
              console.log('[PWA Service] Service Worker registered successfully:', registration.scope);
              
              // Check if there's an update waiting
              if (registration.waiting) {
                this.updateAvailableSubject.next(true);
              }

              resolve();
            })
            .catch((error) => {
              console.error('[PWA Service] Service Worker registration failed:', error);
              reject(error);
            });
        });
      } else {
        console.warn('[PWA Service] Service Workers not supported in this browser');
        resolve();
      }
    });
  }

  /**
   * Prompt user to install the app
   */
  public async promptInstall(): Promise<boolean> {
    if (!this.promptEvent) {
      console.log('[PWA Service] No install prompt available');
      return false;
    }

    try {
      this.promptEvent.prompt();
      const result = await this.promptEvent.userChoice;
      
      console.log('[PWA Service] Install prompt result:', result.outcome);
      
      if (result.outcome === 'accepted') {
        this.isInstalledSubject.next(true);
        this.promptEvent = null;
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[PWA Service] Error showing install prompt:', error);
      return false;
    }
  }

  /**
   * Check if the app can be installed
   */
  public canInstall(): boolean {
    return !!this.promptEvent;
  }

  /**
   * Check if currently online
   */
  public isOnline(): boolean {
    return this.isBrowser ? navigator.onLine : true;
  }

  /**
   * Reload the app to activate the new service worker
   */
  public activateUpdate(): void {
    if (!this.isBrowser) return;
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration && registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          
          // Reload the page after the new service worker takes control
          let refreshing = false;
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (!refreshing) {
              refreshing = true;
              window.location.reload();
            }
          });
        }
      });
    }
  }

  /**
   * Clear all service worker caches
   */
  public async clearCache(): Promise<void> {
    if (!this.isBrowser) return;
    
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('[PWA Service] All caches cleared');
    }
  }

  /**
   * Unregister the service worker
   */
  public async unregisterServiceWorker(): Promise<void> {
    if (!this.isBrowser) return;
    
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.unregister();
        console.log('[PWA Service] Service Worker unregistered');
      }
    }
  }

  /**
   * Get cache size (approximate)
   */
  public async getCacheSize(): Promise<number> {
    if (!this.isBrowser) return 0;
    
    if ('caches' in window && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return estimate.usage || 0;
    }
    return 0;
  }

  /**
   * Request persistent storage
   */
  public async requestPersistentStorage(): Promise<boolean> {
    if (!this.isBrowser) return false;
    
    if ('storage' in navigator && 'persist' in navigator.storage) {
      const isPersisted = await navigator.storage.persist();
      console.log('[PWA Service] Persistent storage:', isPersisted);
      return isPersisted;
    }
    return false;
  }

  /**
   * Check if persistent storage is granted
   */
  public async isPersistentStorage(): Promise<boolean> {
    if (!this.isBrowser) return false;
    
    if ('storage' in navigator && 'persisted' in navigator.storage) {
      return await navigator.storage.persisted();
    }
    return false;
  }

  /**
   * Share content using Web Share API
   */
  public async share(data: { title?: string; text?: string; url?: string }): Promise<boolean> {
    if (!this.isBrowser) return false;
    
    if ('share' in navigator) {
      try {
        await (navigator as any).share(data);
        return true;
      } catch (error) {
        console.error('[PWA Service] Share failed:', error);
        return false;
      }
    }
    return false;
  }

  /**
   * Check if Web Share API is available
   */
  public canShare(): boolean {
    return this.isBrowser && 'share' in navigator;
  }
}

